import {
  AfterContentInit,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faAngleDoubleLeft,
  faBriefcase,
  faUsers,
} from '@fortawesome/pro-solid-svg-icons';
import {
  AaNgModule,
  DisplayStrategy,
  IInstitution,
  IInstitutionContext,
  INSTITUTION_CONTEXT_TOKEN,
  ITabDefinition,
  IUnit,
  PersonProfile as AaNgPersonProfile,
} from 'aa-ng';
import {
  CollaborativeNetworkAndActivitiesListViewType,
  RelevancyProfileView,
} from 'aa-ng-d3';
import { IPerson, mapPersonProfile, PersonProfile } from 'src/app/models';

import { IPersonSearchResult } from '../../models/person-search';
import { BaseDirective } from '../base.component';
import { RelevancyActivitiesComponent } from './relevancy-activities.component';
import { RelevancyCollaborationsComponent } from './relevancy-collaborations.component';

@Component({
  selector: 'app-relevancy-profile',
  imports: [
    AaNgModule,
    FaIconComponent,
    RelevancyActivitiesComponent,
    RelevancyCollaborationsComponent,
  ],
  templateUrl: './relevancy-profile.component.html',
  styles: `
    aa-collaborative-network-and-activities-list {
      aa-activities-menu {
        display: flex;

        button {
          margin: 0 0.5rem 1rem;
        }
      }
    }
  `,
})
export class RelevancyProfileComponent
  extends BaseDirective
  implements AfterContentInit
{
  institutionContext = inject<IInstitutionContext>(INSTITUTION_CONTEXT_TOKEN, {
    optional: true,
  })!;

  personUnits: IUnit[] = [];
  personProfile: AaNgPersonProfile;
  loading = true;

  @Input() set person(person: IPersonSearchResult) {
    this._person = person;
    this.personProfile = mapPersonProfile(person as IPerson as PersonProfile);
    if (person.primaryUnitAffiliation?.name?.length) {
      this.personUnits.push(person.primaryUnitAffiliation);
    }
    if (person.nonPrimaryUnitAffiliations) {
      this.personUnits.concat(person.nonPrimaryUnitAffiliations);
    }
    this.updatePrimaryInstitutionName();
  }
  @Output() viewToggle = new EventEmitter<RelevancyProfileView>();

  RelevancyProfileView = RelevancyProfileView;
  faUsers = faUsers;
  faBriefcase = faBriefcase;
  faAngleDoubleLeft = faAngleDoubleLeft;
  collaborativeNetworkAndActivitiesListCurrentView: CollaborativeNetworkAndActivitiesListViewType;
  relatedNetworksTitle = 'Related Collaborations';

  @ViewChild('collaborationsHeader', { static: true })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  collaborationsHeaderTemplate: TemplateRef<any>;
  @ViewChild('worksHeader', { static: true })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  worksHeaderTemplate: TemplateRef<any>;

  set currentTab(currentTab: RelevancyProfileView) {
    if (currentTab === RelevancyProfileView.Collaborations) {
      this.collaborativeNetworkAndActivitiesListCurrentView =
        CollaborativeNetworkAndActivitiesListViewType.Network;
    } else {
      this.collaborativeNetworkAndActivitiesListCurrentView =
        CollaborativeNetworkAndActivitiesListViewType.List;
    }

    this._currentTab = currentTab;
  }

  _currentTab: RelevancyProfileView;
  institutions: IInstitution[];
  _person: IPersonSearchResult;
  primaryInstitutionName: string;
  tabDefinitions: ITabDefinition[];
  CollaborativeNetworkAndActivitiesListViewType =
    CollaborativeNetworkAndActivitiesListViewType;

  constructor() {
    super();

    if (this.institutionContext) {
      if (this.institutionContext.institutionGroup) {
        this.institutions =
          this.institutionContext.institutionGroup.institutions;
        this.updatePrimaryInstitutionName();
      } else if (this.institutionContext.context) {
        this.institutionContext.context.subscribe((context) => {
          this.institutions =
            (
              !!context.institutionGroup &&
              !!context.institutionGroup.institutions
            ) ?
              context.institutionGroup.institutions
            : null;
          this.updatePrimaryInstitutionName();
        });
      }
    }
  }

  ngAfterContentInit() {
    this.tabDefinitions = [
      {
        displayStrategy: new DisplayStrategy(this.collaborationsHeaderTemplate),
        data: { type: RelevancyProfileView.Collaborations },
        isCurrent: this._currentTab === RelevancyProfileView.Collaborations,
      },
      {
        displayStrategy: new DisplayStrategy(this.worksHeaderTemplate),
        data: { type: RelevancyProfileView.Works },
        isCurrent: this._currentTab === RelevancyProfileView.Works,
      },
    ];
  }

  changeView(tab: ITabDefinition) {
    this.currentTab = tab.data.type;
    this.viewToggle.emit(tab.data.type);
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  private updatePrimaryInstitutionName() {
    if (this.institutions && this.person) {
      this.primaryInstitutionName = this.institutions.find(
        (institution) => institution.id === this.person.primaryInstitution.id,
      ).name;
    }
  }
}
